/**
 * Smoothing utilities for telemetry data processing
 *
 * Provides reusable smoothing algorithms for GPS coordinates and numeric values.
 * Used by multiple processors to reduce noise in telemetry data.
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import {
  GPS_SPEED_MAX_PLAUSIBLE_ACCEL_MS2,
  GPS_SPEED_RESYNC_S,
} from '../analysis/config/gpsSpeedSanitization'

/**
 * Reject supra-physical GPS speed samples and interpolate across them.
 *
 * Complements removeSpeedSpikes: the median despiker catches ISOLATED
 * outliers, but a receiver dropout can report garbage for a RUN of samples
 * (session-143033: 0.0 km/h for 8 consecutive 25 Hz samples mid-straight,
 * implied rates 50–130 m/s²) — the local median is then itself corrupted and
 * the run sails through.
 *
 * Chain-anchored rejection: walk forward keeping the last ACCEPTED sample as
 * anchor; reject a sample when its implied |dv/dt| versus the anchor exceeds
 * GPS_SPEED_MAX_PLAUSIBLE_ACCEL_MS2. dt keeps growing while samples are
 * rejected, so recovery self-heals — after the glitch, the next honest
 * sample is accepted because its dv over the longer dt is plausible. When
 * the anchor is older than GPS_SPEED_RESYNC_S the stream resyncs (accepts
 * unconditionally) rather than extrapolating a stale state. Rejected runs
 * are linearly interpolated in time between the bounding accepted samples
 * (held flat when the run touches the stream end).
 *
 * Known limitation: a corrupted FIRST sample anchors the chain and can
 * poison up to GPS_SPEED_RESYNC_S of stream start; sessions begin at rest in
 * practice, and the median despiker downstream still covers isolated cases.
 *
 * @param speedsKmh - Speed values (km/h), parallel to timestampsMs
 * @param timestampsMs - Sample timestamps (epoch ms, ascending)
 * @returns The INPUT array (same reference) when nothing is rejected — a
 *   provable no-op for clean traces — otherwise a new sanitized array.
 */
export function sanitizeSupraPhysicalSpeeds(
  speedsKmh: number[],
  timestampsMs: number[]
): number[] {
  const n = speedsKmh.length
  if (n < 2) return speedsKmh

  const rejected: boolean[] = new Array(n).fill(false)
  let anyRejected = false
  let lastGood = 0
  for (let i = 1; i < n; i++) {
    const dtS = (timestampsMs[i] - timestampsMs[lastGood]) / 1000
    if (dtS <= 0) {
      rejected[i] = true
      anyRejected = true
      continue
    }
    if (dtS > GPS_SPEED_RESYNC_S) {
      lastGood = i
      continue
    }
    const dvMs = (speedsKmh[i] - speedsKmh[lastGood]) / 3.6
    if (Math.abs(dvMs / dtS) > GPS_SPEED_MAX_PLAUSIBLE_ACCEL_MS2) {
      rejected[i] = true
      anyRejected = true
    } else {
      lastGood = i
    }
  }
  if (!anyRejected) return speedsKmh

  const result = [...speedsKmh]
  let i = 0
  while (i < n) {
    if (!rejected[i]) {
      i++
      continue
    }
    const runStart = i
    while (i < n && rejected[i]) i++
    const leftIdx = runStart - 1 // >= 0: index 0 is never rejected
    const rightIdx = i // may be n when the run reaches the end
    for (let j = runStart; j < rightIdx; j++) {
      if (rightIdx >= n) {
        result[j] = result[leftIdx]
      } else {
        const t0 = timestampsMs[leftIdx]
        const t1 = timestampsMs[rightIdx]
        const fraction = t1 > t0 ? (timestampsMs[j] - t0) / (t1 - t0) : 0
        result[j] =
          result[leftIdx] + fraction * (speedsKmh[rightIdx] - result[leftIdx])
      }
    }
  }
  return result
}

/**
 * Remove speed spikes caused by GPS noise.
 *
 * Detects individual readings where speed deviates significantly from the
 * local median and replaces them with linearly interpolated values.
 * This prevents unrealistic acceleration/G-force values downstream.
 *
 * Uses a sliding window median comparison — a spike is a point whose speed
 * differs from the local median by more than a threshold (absolute km/h or
 * relative percentage, whichever is larger). The threshold is generous enough
 * to preserve real braking/acceleration events while catching GPS glitches.
 *
 * @param speeds - Array of speed values (km/h)
 * @param windowSize - Number of neighboring points to consider (default: 5)
 * @param maxDeviationKmh - Absolute deviation threshold in km/h (default: 30)
 * @param maxDeviationFraction - Relative deviation threshold as fraction (default: 0.25)
 * @returns New array with spikes replaced by interpolated values
 */
export function removeSpeedSpikes(
  speeds: number[],
  windowSize: number = 5,
  maxDeviationKmh: number = 30,
  maxDeviationFraction: number = 0.25
): number[] {
  if (speeds.length < windowSize) return [...speeds]

  const result = [...speeds]
  const halfWindow = Math.floor(windowSize / 2)

  // First pass: identify spikes by comparing each point to its local median
  const spikeIndices: number[] = []

  for (let i = 0; i < speeds.length; i++) {
    const start = Math.max(0, i - halfWindow)
    const end = Math.min(speeds.length, i + halfWindow + 1)

    // Get local window (excluding the point itself to avoid self-influence)
    const neighbors: number[] = []
    for (let j = start; j < end; j++) {
      if (j !== i) neighbors.push(speeds[j])
    }

    if (neighbors.length < 2) continue

    neighbors.sort((a, b) => a - b)
    const median = neighbors[Math.floor(neighbors.length / 2)]

    const deviation = Math.abs(speeds[i] - median)
    // Dynamic threshold: max of absolute and relative deviation
    const threshold = Math.max(maxDeviationKmh, median * maxDeviationFraction)

    if (deviation > threshold) {
      spikeIndices.push(i)
    }
  }

  // Second pass: replace spikes with linearly interpolated values
  for (const idx of spikeIndices) {
    // Find nearest non-spike neighbors on each side
    let left = idx - 1
    while (left >= 0 && spikeIndices.includes(left)) left--

    let right = idx + 1
    while (right < speeds.length && spikeIndices.includes(right)) right++

    if (left >= 0 && right < speeds.length) {
      // Interpolate between neighbors
      const fraction = (idx - left) / (right - left)
      result[idx] = speeds[left] + fraction * (speeds[right] - speeds[left])
    } else if (left >= 0) {
      result[idx] = speeds[left]
    } else if (right < speeds.length) {
      result[idx] = speeds[right]
    }
    // else: isolated point, leave as-is
  }

  return result
}

/**
 * Simple moving average smoothing for numeric arrays
 *
 * @param values - Array of values to smooth
 * @param windowSize - Size of the smoothing window (must be >= 2)
 * @returns Smoothed values array
 */
export function smoothValues(values: number[], windowSize: number): number[] {
  if (values.length === 0 || windowSize < 2) return values

  const smoothed: number[] = []
  const halfWindow = Math.floor(windowSize / 2)

  for (let i = 0; i < values.length; i++) {
    const start = Math.max(0, i - halfWindow)
    const end = Math.min(values.length, i + halfWindow + 1)

    let sum = 0
    for (let j = start; j < end; j++) {
      sum += values[j]
    }

    smoothed.push(sum / (end - start))
  }

  return smoothed
}

/**
 * Multi-pass moving average smoothing for noisy data
 *
 * GPS-derived values (acceleration, etc.) are inherently noisy and need
 * aggressive smoothing. Multiple passes provide stronger smoothing while
 * maintaining signal shape better than a single large window.
 *
 * @param values - Array of values to smooth
 * @param windowSize - Size of the smoothing window per pass
 * @param passes - Number of smoothing passes (default: 1)
 * @returns Smoothed values array
 */
export function smoothWithPasses(
  values: number[],
  windowSize: number,
  passes: number = 1
): number[] {
  let smoothed = [...values]

  for (let pass = 0; pass < passes; pass++) {
    const temp: number[] = []
    const halfWindow = Math.floor(windowSize / 2)

    for (let i = 0; i < smoothed.length; i++) {
      const start = Math.max(0, i - halfWindow)
      const end = Math.min(smoothed.length, i + halfWindow + 1)

      let sum = 0
      let count = 0

      for (let j = start; j < end; j++) {
        sum += smoothed[j]
        count++
      }

      temp.push(count > 0 ? sum / count : 0)
    }

    smoothed = temp
  }

  return smoothed
}

/**
 * Gaussian-weighted smoothing for numeric arrays
 *
 * Uses Gaussian weights so points closer to the center contribute more.
 * This reduces noise while preserving peaks better than uniform averaging.
 * Multiple passes approximate a wider Gaussian for stronger smoothing.
 *
 * Particularly effective for acceleration data where numerical differentiation
 * amplifies noise — Gaussian weighting avoids the ripple artifacts that
 * uniform (box) filters create.
 *
 * @param values - Array of values to smooth
 * @param windowSize - Size of the smoothing window
 * @param passes - Number of smoothing passes (default: 1)
 * @returns Smoothed values array
 */
export function smoothValuesGaussian(
  values: number[],
  windowSize: number,
  passes: number = 1
): number[] {
  if (values.length === 0 || windowSize < 2) return values

  let smoothed = [...values]
  const halfWindow = Math.floor(windowSize / 2)
  const sigma = windowSize / 4

  // Pre-compute Gaussian weights for the window
  const weights: number[] = []
  for (let d = 0; d <= halfWindow; d++) {
    weights.push(Math.exp(-(d * d) / (2 * sigma * sigma)))
  }

  for (let pass = 0; pass < passes; pass++) {
    const temp: number[] = []

    for (let i = 0; i < smoothed.length; i++) {
      const windowStart = Math.max(0, i - halfWindow)
      const windowEnd = Math.min(smoothed.length, i + halfWindow + 1)

      let sum = 0
      let weightSum = 0

      for (let j = windowStart; j < windowEnd; j++) {
        const w = weights[Math.abs(j - i)]
        sum += smoothed[j] * w
        weightSum += w
      }

      temp.push(weightSum > 0 ? sum / weightSum : 0)
    }

    smoothed = temp
  }

  return smoothed
}

/**
 * Gaussian-weighted coordinate smoothing for GPS points
 *
 * Uses Gaussian weights to give more importance to points closer to the center.
 * This reduces GPS noise while preserving trajectory shape better than uniform
 * averaging. Essential for accurate turn radius and lean angle calculations.
 *
 * @param points - GPS points to smooth
 * @param windowSize - Size of the smoothing window
 * @returns Smoothed GPS points (only lat/lon modified, other fields preserved)
 */
export function smoothGpsCoordinates(
  points: GpsPoint[],
  windowSize: number
): GpsPoint[] {
  if (points.length < windowSize) return points

  const smoothed: GpsPoint[] = []
  const halfWindow = Math.floor(windowSize / 2)

  for (let i = 0; i < points.length; i++) {
    const windowStart = Math.max(0, i - halfWindow)
    const windowEnd = Math.min(points.length, i + halfWindow + 1)

    let latSum = 0
    let lonSum = 0
    let weightSum = 0

    for (let j = windowStart; j < windowEnd; j++) {
      const distance = Math.abs(j - i)
      // Gaussian weight: closer points get more weight
      const weight = Math.exp(
        -(distance * distance) / (2 * (windowSize / 4) ** 2)
      )
      latSum += points[j].latitude * weight
      lonSum += points[j].longitude * weight
      weightSum += weight
    }

    smoothed.push({
      ...points[i],
      latitude: latSum / weightSum,
      longitude: lonSum / weightSum,
    })
  }

  return smoothed
}
